import { t } from 'i18next';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

import { PlanScenarioParams } from 'constants/routes';
import { useFeatureFlags } from 'contexts/FeatureFlags';
import { canViewCollaboratorResource } from 'hooks/canViewCollaboratorResource';
import { useIsSpmCollaborationUser } from 'hooks/useIsSpmCollaborationUser';
import { Box, Spinner, Text } from 'quotient';
import { SPMModelsService, UsersService } from 'services';
import { SPMWorksheetService } from 'services/SPM/SPMWorksheetService';
import { PlanningCycle } from 'types/planningCycles';
import {
  NAVIGATION_ANIMATION_DURATION,
  SPMModel,
  SPMModelType,
  SPMScenario,
  SPMWorksheet,
  SPMWorksheetLayerType,
  SPMWorksheetType,
} from 'types/SPM';

export type SPMPlanContextType = {
  planningCycle: PlanningCycle;
  setPlanningCycle: Dispatch<SetStateAction<PlanningCycle>>;
  scenarios: SPMScenario[];
  activeScenarioId: string | undefined;
  capacityModel: SPMModel;
  quotaModel: SPMModel;
  capacityWorksheets: SPMWorksheet[];
  quotaWorksheets: SPMWorksheet[];
  baseScenario: SPMScenario;
  canViewCapacityModel: boolean;
  canViewQuotaModel: boolean;
  planMetricsWorksheet?: SPMWorksheet;
  rosterWorksheet: SPMWorksheet | undefined;
  worksheetsFilter: string | undefined;
  setWorksheetsFilter: Dispatch<SetStateAction<string | undefined>>;
  isNavigationCollapsed: boolean;
  isAnimating: boolean;
  toggleNavigation: () => void;
  focusedWorksheet: string | undefined;
  focusWorksheet: (worksheetId: string) => void;
  unfocusWorksheet: () => void;
};

const SPMPlanContext = createContext<SPMPlanContextType>({} as SPMPlanContextType);
export const useSPMPlanContext = () => useContext(SPMPlanContext);

type Props = {
  children: ReactNode;
  planningCycle: PlanningCycle;
  scenarios: SPMScenario[];
};

export const SPMPlanContextProvider = ({
  children,
  planningCycle: planningCycleProp,
  scenarios: scenariosProp,
}: Props) => {
  const { scenarioId, planningCycleId } = useParams<PlanScenarioParams>();
  const { query: permissionsQuery } = UsersService.useGetCurrentUserPermissions();
  const userPermissions = permissionsQuery.data;
  const { isFeatureEnabled } = useFeatureFlags();
  const isPermissionsV2Enabled = isFeatureEnabled('feature_flag_spm_permissions_v2');
  const isCollaborationUser = useIsSpmCollaborationUser(userPermissions);

  const [planningCycle, setPlanningCycle] = useState<PlanningCycle>(planningCycleProp);
  // These models do not contain the columns for the worksheets as that was too much for the planning cycle call. Marking
  // them as internal and refetching with the spmmodelquery below to retrieve the actual columns.
  const _capacityModel = planningCycle.spm_models.find((model) => model.model_type === SPMModelType.CAPACITY);
  const _quotaModel = planningCycle.spm_models.find((model) => model.model_type === SPMModelType.QUOTA);

  const baseScenario = scenariosProp.find((s) => s.is_system) || scenariosProp[0];
  const activeScenarioId = useMemo(() => {
    if (scenarioId) {
      return scenarioId;
    }
    if (userPermissions && planningCycleId) {
      const scenarioIds = new Set(scenariosProp.map((scenario) => scenario.id));
      const sharedViewGrants = userPermissions.shared_resources.filter(
        (sharedResource) =>
          sharedResource.resource_type === 'spm_shared_view' &&
          sharedResource.resource_path[0] === planningCycleId &&
          sharedResource.resource_path.length >= 2,
      );
      const grantMatchingKnownScenario = sharedViewGrants.find((sharedResource) =>
        scenarioIds.has(sharedResource.resource_path[1]),
      );
      if (grantMatchingKnownScenario) {
        return grantMatchingKnownScenario.resource_path[1];
      }
      if (sharedViewGrants[0]) {
        return sharedViewGrants[0].resource_path[1];
      }
    }
    return baseScenario?.id;
  }, [scenarioId, userPermissions, planningCycleId, baseScenario?.id, scenariosProp]);

  const canViewCapacityModel = useMemo(() => {
    if (!_capacityModel?.id) {
      return false;
    }
    return canViewCollaboratorResource({
      isCollaborationUser,
      userPermissions,
      planningCycleId,
      activeScenarioId,
      isPermissionsV2Enabled,
      modelId: _capacityModel.id,
      modelType: 'spm_model',
    });
  }, [
    _capacityModel?.id,
    isCollaborationUser,
    userPermissions,
    planningCycleId,
    activeScenarioId,
    isPermissionsV2Enabled,
  ]);

  const canViewQuotaModel = useMemo(() => {
    if (!_quotaModel?.id) {
      return false;
    }
    return canViewCollaboratorResource({
      isCollaborationUser,
      userPermissions,
      planningCycleId,
      activeScenarioId,
      isPermissionsV2Enabled,
      modelId: _quotaModel.id,
      modelType: 'spm_model',
    });
  }, [
    _quotaModel?.id,
    isCollaborationUser,
    userPermissions,
    planningCycleId,
    activeScenarioId,
    isPermissionsV2Enabled,
  ]);

  // This is used to filter the worksheets either at the plan level or at the model level
  const [worksheetsFilter, setWorksheetsFilter] = useState<string | undefined>(undefined);

  const [isNavigationCollapsed, setIsNavigationCollapsed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  // This is used to track the focused worksheet when the user clicks on the worksheet resizer
  const [focusedWorksheet, setFocusedWorksheet] = useState<string | undefined>(undefined);
  const { query: worksheetsQuery } = SPMWorksheetService.useListSPMWorksheetsQuery({
    planningCycleId: planningCycleProp.id,
  });
  const { query: capacityModelQuery } = SPMModelsService.useGetSPMModelQuery(_capacityModel?.id as string, {
    enabled: !!_capacityModel?.id && canViewCapacityModel,
  });
  const { query: quotaModelQuery } = SPMModelsService.useGetSPMModelQuery(_quotaModel?.id as string, {
    enabled: !!_quotaModel?.id && canViewQuotaModel,
  });
  const { query: capacityWorksheetsQuery } = SPMWorksheetService.useListSPMWorksheetsQuery(
    { planningCycleId: planningCycleProp.id, spmModelId: _capacityModel?.id, scenarioId: activeScenarioId },
    { enabled: !!_capacityModel?.id && canViewCapacityModel },
  );
  const { query: quotaWorksheetsQuery } = SPMWorksheetService.useListSPMWorksheetsQuery(
    { planningCycleId: planningCycleProp.id, spmModelId: _quotaModel?.id, scenarioId: activeScenarioId },
    { enabled: !!_quotaModel?.id && canViewQuotaModel },
  );

  const planMetricsWorksheet = useMemo(
    () =>
      worksheetsQuery.data?.data.find(
        (ws) => ws.type === SPMWorksheetType.METRICS && ws.layer === SPMWorksheetLayerType.SCENARIO,
      ),
    [worksheetsQuery.data?.data],
  );

  const rosterWorksheet = useMemo(
    () => capacityWorksheetsQuery.data?.data.find((ws) => ws.type === SPMWorksheetType.ROSTER),
    [capacityWorksheetsQuery.data?.data],
  );

  // Update planningCycle state when the prop changes (e.g., when query is invalidated in parts of the app)
  useEffect(() => {
    setPlanningCycle(planningCycleProp);
  }, [planningCycleProp]);

  const toggleNavigation = useCallback(() => {
    if (isAnimating) {
      // Prevent toggling during animation
      return;
    }

    setIsAnimating(true);
    setIsNavigationCollapsed(!isNavigationCollapsed);

    // Reset animation state after transition completes which lasts 500ms
    setTimeout(() => setIsAnimating(false), NAVIGATION_ANIMATION_DURATION);
  }, [isAnimating, isNavigationCollapsed, setIsAnimating, setIsNavigationCollapsed]);

  const focusWorksheet = useCallback(
    (worksheetId: string) => {
      setFocusedWorksheet(worksheetId);
      if (!isNavigationCollapsed) {
        // Collapse navigation when focusing a worksheet
        toggleNavigation();
      }
    },
    [isNavigationCollapsed, toggleNavigation],
  );

  const unfocusWorksheet = useCallback(() => {
    setFocusedWorksheet(undefined);
    if (isNavigationCollapsed) {
      // Expand navigation when unfocusing a worksheet
      toggleNavigation();
    }
  }, [isNavigationCollapsed, toggleNavigation]);

  const capacityModel = capacityModelQuery.data ?? _capacityModel;
  const quotaModel = quotaModelQuery.data ?? _quotaModel;

  const contextValue = useMemo(
    () => ({
      planningCycle,
      setPlanningCycle,
      capacityModel,
      quotaModel,
      activeScenarioId,
      capacityWorksheets: capacityWorksheetsQuery.data?.data ?? [],
      quotaWorksheets: quotaWorksheetsQuery.data?.data ?? [],
      scenarios: scenariosProp,
      baseScenario,
      canViewCapacityModel,
      canViewQuotaModel,
      worksheetsFilter,
      setWorksheetsFilter,
      isNavigationCollapsed,
      isAnimating,
      toggleNavigation,
      focusedWorksheet,
      focusWorksheet,
      unfocusWorksheet,
      planMetricsWorksheet,
      rosterWorksheet,
    }),
    [
      planningCycle,
      setPlanningCycle,
      capacityModel,
      quotaModel,
      activeScenarioId,
      capacityWorksheetsQuery.data?.data,
      quotaWorksheetsQuery.data?.data,
      scenariosProp,
      baseScenario,
      canViewCapacityModel,
      canViewQuotaModel,
      worksheetsFilter,
      setWorksheetsFilter,
      isNavigationCollapsed,
      isAnimating,
      toggleNavigation,
      focusedWorksheet,
      focusWorksheet,
      unfocusWorksheet,
      planMetricsWorksheet,
      rosterWorksheet,
    ],
  );

  // Hold the plan shell until permissions load for collaborators so canView* gates are accurate.
  const isPermissionsLoading = isCollaborationUser && permissionsQuery.isLoading;

  const isLoading =
    isPermissionsLoading ||
    (canViewCapacityModel && (capacityModelQuery.isLoading || capacityWorksheetsQuery.isLoading)) ||
    (canViewQuotaModel && (quotaModelQuery.isLoading || quotaWorksheetsQuery.isLoading));
  if (isLoading) {
    return (
      <Box alignItems="center" display="flex" h="full" justifyContent="center" w="full">
        <Spinner />
      </Box>
    );
  }

  if (!capacityModel || !quotaModel) {
    return (
      <Box p={8} textAlign="center">
        <Text>{t('SPM.PlanDetailView.error')}</Text>
      </Box>
    );
  }

  if ((canViewCapacityModel && !capacityModelQuery.data) || (canViewQuotaModel && !quotaModelQuery.data)) {
    return (
      <Box p={8} textAlign="center">
        <Text>{t('SPM.PlanDetailView.error')}</Text>
      </Box>
    );
  }

  return <SPMPlanContext.Provider value={contextValue as SPMPlanContextType}>{children}</SPMPlanContext.Provider>;
};
